import React, { useState, useEffect } from 'react'
import {Rocket, Send} from 'lucide-react'
import { useReviews, Review } from '../hooks/useReviews'
import { useUserProfile } from '../hooks/useUserProfile'

interface ReviewSectionProps {
  projectId: string
  currentUserId?: string
}

export function ReviewSection({ projectId, currentUserId }: ReviewSectionProps) {
  const { reviews, averageRating, loading, addReview } = useReviews(projectId)
  const { profile, fetchUserProfile, canInteract } = useUserProfile()
  const [selectedRating, setSelectedRating] = useState(0)
  const [reviewText, setReviewText] = useState('')
  const [hoveredRating, setHoveredRating] = useState(0)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (currentUserId) {
      fetchUserProfile(currentUserId)
    }
  }, [currentUserId])

  const handleSubmitReview = async () => {
    if (!currentUserId) {
      setError('Please sign in to submit a review')
      return
    }

    if (!canInteract()) {
      setError('Subscribe to unlock commenting and review features ($5/month)')
      return
    }

    if (selectedRating === 0) {
      setError('Please select a rating')
      return
    }

    if (!reviewText.trim()) {
      setError('Please write a review')
      return
    }

    setSubmitting(true)
    setError('')

    try {
      await addReview({
        projectId,
        userId: currentUserId,
        userName: profile?.userName || 'Anonymous',
        userProfileImageUrl: profile?.profileImageUrl,
        rating: selectedRating,
        content: reviewText.trim()
      })

      // Reset form
      setSelectedRating(0)
      setReviewText('')
      setError('')
    } catch (err: any) {
      setError(err.message || 'Failed to submit review')
    } finally {
      setSubmitting(false)
    }
  }

  const RocketRating = ({ rating, interactive = false }: { rating: number; interactive?: boolean }) => {
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((rocket) => {
          const isFilled = interactive
            ? (hoveredRating || selectedRating) >= rocket
            : rating >= rocket

          return (
            <button
              key={rocket}
              disabled={!interactive}
              onClick={() => interactive && setSelectedRating(rocket)}
              onMouseEnter={() => interactive && setHoveredRating(rocket)}
              onMouseLeave={() => interactive && setHoveredRating(0)}
              className={`transition-all ${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}
            >
              <Rocket
                className={`w-6 h-6 ${
                  isFilled
                    ? 'fill-[#0052FF] text-[#0052FF]'
                    : 'text-gray-600'
                } transition-colors`}
              />
            </button>
          )
        })}
      </div>
    )
  }

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-white">Reviews</h2>
        {reviews.length > 0 && (
          <div className="flex items-center gap-3">
            <RocketRating rating={averageRating} />
            <span className="text-gray-300 text-lg">
              {averageRating.toFixed(1)} ({reviews.length} {reviews.length === 1 ? 'review' : 'reviews'})
            </span>
          </div>
        )}
      </div>

      {/* Submit Review Form */}
      {currentUserId && (
        <div className="mb-8 p-4 bg-white/5 border border-white/10 rounded-lg">
          <h3 className="text-lg font-semibold text-white mb-4">Rate this project</h3>
          
          {/* Rocket Rating Selector */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2">Your Rating</label>
            <RocketRating rating={selectedRating} interactive={true} />
          </div>

          {/* Review Text */}
          <div className="mb-4">
            <label className="block text-gray-300 text-sm mb-2">Your Review</label>
            <textarea
              value={reviewText}
              onChange={(e) => setReviewText(e.target.value)}
              placeholder="Share your thoughts about this project..."
              className="w-full px-4 py-3 bg-[#0A0E27] border border-white/10 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-[#0052FF] resize-none"
              rows={4}
            />
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Submit Button */}
          <button
            onClick={handleSubmitReview}
            disabled={submitting || !selectedRating || !reviewText.trim()}
            className="flex items-center gap-2 px-6 py-2 bg-gradient-to-r from-[#0052FF] to-[#60A5FA] text-white rounded-lg font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-4 h-4" />
            {submitting ? 'Submitting...' : 'Submit Review'}
          </button>
        </div>
      )}

      {/* Reviews List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#0052FF]"></div>
          <p className="text-gray-400 mt-2">Loading reviews...</p>
        </div>
      ) : reviews.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-400">No reviews yet. Be the first to review!</p>
        </div>
      ) : (
        <div className="space-y-4">
          {reviews.map((review) => (
            <div
              key={review._id}
              className="p-4 bg-white/5 border border-white/10 rounded-lg hover:border-[#0052FF]/50 transition-colors"
            >
              <div className="flex items-start gap-4">
                {/* User Avatar */}
                {review.userProfileImageUrl ? (
                  <img
                    src={review.userProfileImageUrl}
                    alt={review.userName}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#0052FF] to-[#60A5FA] flex items-center justify-center">
                    <span className="text-white font-bold text-lg">
                      {review.userName.charAt(0).toUpperCase()}
                    </span>
                  </div>
                )}

                {/* Review Content */}
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-2">
                    <div>
                      <h4 className="text-white font-semibold">{review.userName}</h4>
                      <RocketRating rating={review.rating} />
                    </div>
                    {review.createdAt && (
                      <span className="text-gray-500 text-sm">
                        {new Date(review.createdAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-gray-300">{review.content}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
